import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Textarea, useToast } from '../../components/common';
import { SegmentedControl } from '@ofself/shell';
import RealmSchemaEditor from '../../components/RealmSchemaEditor/RealmSchemaEditor';
import { Activity, AlertTriangle, Database, Plug, Users, Zap } from 'lucide-react';
import { buildDLRFromContract, VERB_LABELS } from '../../constants/formA';
import { dlrInputsToEditorState, editorStateToDlrInputs,
         dlrInputsToToolModes, applyToolModesToDlrInputs } from '../../utils/dlrEditorBridge';
import { generateDLRYaml } from '../../utils/dlrYaml';
import api from '../../services/api';
import './AccessTab.css';

/**
 * What the app asks people to grant — the exposure profile, in its own place.
 *
 * The editor and the YAML sit side by side and stay in step. Editing the controls
 * rewrites the YAML on the next keystroke; editing the YAML and applying it
 * rebuilds the controls. They are two views of one document, not two documents.
 *
 * YAML is applied through the server's own parser rather than a local one: the CLI
 * writes schema *names* and only the backend resolves those to ids, and it is the
 * same parser the save path uses — so what you preview is what persists.
 */
/**
 * What a save actually changed, in words, derived from the two versions.
 *
 * This used to be a "What changed?" box the developer typed into. It was
 * optional, so it was usually blank, and the record already knows: the old and
 * new request lists are right there to compare. A summary nobody has to write
 * is one that is always present and always true.
 */
function summariseChange(before, after, schemaName) {
  const key = it => `${it.resource || 'nodes'}:${it.verb || 'read'}`;
  const label = it => {
    const ids = it.schema_ids || it.schemas || [];
    // An id that does not resolve is shown truncated — a full UUID in a sentence
    // is noise, and the registry may simply not have loaded yet.
    const names = ids.map(id => schemaName[String(id)] || `${String(id).slice(0, 8)}…`).filter(Boolean);
    return names.length ? `${key(it)} on ${names.slice(0, 3).join(', ')}${names.length > 3 ? `, +${names.length - 3}` : ''}`
                        : key(it);
  };
  const index = list => new Map((list || []).map(it => [
    `${key(it)}|${JSON.stringify((it.schema_ids || it.schemas || []).map(String).sort())}`, it,
  ]));
  const a = index(before), b = index(after);
  const added = [...b].filter(([k]) => !a.has(k)).map(([, it]) => label(it));
  const removed = [...a].filter(([k]) => !b.has(k)).map(([, it]) => label(it));

  const parts = [];
  if (added.length) parts.push(`added ${added.slice(0, 3).join('; ')}${added.length > 3 ? ` and ${added.length - 3} more` : ''}`);
  if (removed.length) parts.push(`removed ${removed.slice(0, 3).join('; ')}${removed.length > 3 ? ` and ${removed.length - 3} more` : ''}`);
  if (!parts.length) return 'Updated the access request';
  return `Access: ${parts.join(', ')}`;
}

function AppAccessTab({ app, appSchemas, availablePlugins = [], onSaved, onStaged }) {
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    dlr_inputs: app.dlr_inputs || app.node_requirements || [],
    input_parameters: app.input_parameters || [],
    schema_grants: app.data_lender_request?.schema_grants || null,
  }));
  const [editorKey, setEditorKey] = useState(0);
  const [draftYaml, setDraftYaml] = useState(null);   // non-null while hand-editing
  const [yamlErrors, setYamlErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  // Which view. Remembered, because whether you think in controls or in YAML is
  // a habit, not a per-visit decision.
  const [view, setView] = useState(() => {
    // Controls by default. 'Both' gave half the screen to a text view most
    // people never type in, and squeezed the one they do.
    try { return localStorage.getItem('accessView') || 'visual'; } catch { return 'visual'; }
  });
  useEffect(() => { try { localStorage.setItem('accessView', view); } catch { /* ignore */ } }, [view]);
  // 'ok' | 'checking' | 'bad' — the YAML's standing, kept current as you type.
  const [yamlState, setYamlState] = useState('ok');
  const [viewingStaged, setViewingStaged] = useState(false);

  // Load the staged access request over the live one, same as the CRUX tab.
  // Showing the live DLR while a staged one existed made staged work look
  // discarded, and staging again would have overwritten it from the live base.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.request(`/third-party/${app.id}/draft`);
        const stagedYaml = (d?.changes || []).find(c => c.field === 'yaml')?.to;
        if (cancelled || !stagedYaml) return;
        const parsed = await api.request('/third-party/parse-yaml', {
          method: 'POST', body: JSON.stringify({ yaml: stagedYaml }),
        });
        if (cancelled || parsed.errors?.length) return;
        setForm(prev => ({
          ...prev,
          dlr_inputs: parsed.dlr_inputs || [],
          input_parameters: parsed.input_parameters || [],
          ...(parsed.schema_grants ? { schema_grants: parsed.schema_grants } : {}),
        }));
        setEditorKey(k => k + 1);
        setViewingStaged(true);
      } catch { /* no draft, or an older backend: the live request stands */ }
    })();
    return () => { cancelled = true; };
  }, [app.id]);
  const [baseline] = useState(() => JSON.stringify(app.dlr_inputs || []));

  const editorState = useMemo(
    () => dlrInputsToEditorState(form.dlr_inputs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorKey]
  );

  const schemaNameMap = useMemo(
    () => Object.fromEntries((appSchemas || []).map(s => [String(s.id), s.schema_name])),
    [appSchemas]
  );

  // The generated YAML — always derived from the controls, so it cannot drift.
  const generated = useMemo(() => {
    try {
      return generateDLRYaml(buildDLRFromContract(form), {
        dlrInputs: form.dlr_inputs || [],
        inputParameters: form.input_parameters || [],
        schemaGrants: form.schema_grants || null,
        schemaNameMap,
      });
    } catch {
      return '';
    }
  }, [form, schemaNameMap]);

  // Changing the controls makes the generated YAML the truth again, so a stale
  // hand-edit is dropped rather than silently kept.
  useEffect(() => { setDraftYaml(null); setYamlErrors([]); setYamlState('ok'); }, [form.dlr_inputs]);

  /**
   * Validate — and, when it parses, adopt — the hand-edited YAML.
   *
   * Parsing happens on the server for a reason: the CLI writes schema *names*
   * and only the backend resolves those to ids, and it is the same parser the
   * save path uses. So a green tick here means the save will read it the same
   * way, not that a local guess liked the shape.
   */
  const checkYaml = useCallback(async (text, { adopt }) => {
    setYamlState('checking');
    try {
      const res = await api.request('/third-party/parse-yaml', {
        method: 'POST',
        body: JSON.stringify({ yaml: text }),
      });
      if (res.errors?.length) { setYamlErrors(res.errors); setYamlState('bad'); return false; }
      setYamlErrors([]);
      setYamlState('ok');
      if (adopt) {
        setForm(prev => ({
          ...prev,
          dlr_inputs: res.dlr_inputs || [],
          input_parameters: res.input_parameters || [],
          ...(res.schema_grants ? { schema_grants: res.schema_grants } : {}),
        }));
        setEditorKey(k => k + 1);
        setDraftYaml(null);
      }
      return true;
    } catch (err) {
      setYamlErrors(err.body?.errors?.length ? err.body.errors : [err.message || 'Could not read that YAML.']);
      setYamlState('bad');
      return false;
    }
  }, []);

  // Typing in the YAML validates as you go, and adopts once it parses — the two
  // views are one document, so a valid edit on either side is the document.
  // Debounced, because every keystroke would otherwise be a request, and most
  // keystrokes are the middle of a word.
  useEffect(() => {
    if (draftYaml == null) return;
    setYamlState('checking');
    const t = setTimeout(() => { checkYaml(draftYaml, { adopt: true }); }, 650);
    return () => clearTimeout(t);
  }, [draftYaml, checkYaml]);

  const dirty = JSON.stringify(form.dlr_inputs || []) !== baseline;

  const save = async () => {
    // An app's draft replays through the APP update handler, which skips a
    // plugin's versioning, secret check and lapse notices. Never for a plugin.
    if (isPluginRow(app)) return;
    setSaving(true);
    try {
      // Staged, like every other edit on this app: the access request is the
      // change most worth reviewing beside the others before it goes live.
      await api.request(`/third-party/${app.id}/draft`, {
        method: 'PATCH',
        body: JSON.stringify({ yaml: generated }),
      });
      toast.success('Access request staged.');
      setViewingStaged(true);
      onStaged?.();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Could not stage.');
    } finally {
      setSaving(false);
    }
  };

  const count = (form.dlr_inputs || []).length;

  // Back to the state this tab opened with — the baseline the change summary is
  // measured against — without a page reload.
  const reset = () => {
    setForm({
      dlr_inputs: JSON.parse(baseline),
      input_parameters: app.input_parameters || [],
      schema_grants: app.data_lender_request?.schema_grants || null,
    });
    setEditorKey(k => k + 1);
    setDraftYaml(null);
    setYamlErrors([]);
    setYamlState('ok');
  };

  const VIEWS = [
    { value: 'visual', label: 'Controls' },
    { value: 'split', label: 'Both' },
    { value: 'code', label: 'YAML' },
  ];

  const editRef = useRef(null);
  const toEditor = () => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const dlr = app.data_lender_request || {};
  const isOpen = dlr.open === true || dlr.is_open === true;

  return (
    <div className="access">
      {/* What a person is asked, in the consent screen's words, before any control. */}
      <section className="access__band">
        <h3 className="access__title">
          What people are asked
          {count > 0 && <span className="access__n">{count}</span>}
          {dirty && <span className="access__dirty">unsaved</span>}
        </h3>
        <p className="access__lede">
          Each line is one thing a person approves before the app can run, in the words the consent screen uses.
        </p>
        {count === 0 ? (
          isOpen ? (
            <p className="access__open">
              An open request: it asks for nothing up front and works with whatever a person chooses to grant.
            </p>
          ) : (
            <div className="access__empty" role="status">
              <span className="access__empty-ic" aria-hidden="true"><AlertTriangle size={17} /></span>
              <div>
                <b>It asks for nothing</b>
                <span>
                  Nobody can grant an app that asks for nothing, so no one can use {app.name || 'it'} yet.
                  Add the data its agents read and write.
                </span>
              </div>
              <Button onClick={toEditor}>Add the first request</Button>
            </div>
          )
        ) : (
          <AskList inputs={form.dlr_inputs || []} schemaNameMap={schemaNameMap} plugins={availablePlugins} />
        )}
      </section>

      {viewingStaged && (
        <p className="access__staged-note">
          You are editing the staged access request, not the one people are being asked to approve.
          It goes live when you commit.
        </p>
      )}

      <section className="access__band access__band--edit" ref={editRef}>
        <div className="access__bar">
          <h3 className="access__title">Edit</h3>
          <SegmentedControl options={VIEWS} value={view} onChange={setView} />
          <p className="access__explain">
            {view === 'visual' && 'Press a verb to ask for it. The YAML follows.'}
            {view === 'split' && 'Two views of one request. Edit either side and the other follows.'}
            {view === 'code' && 'The same text paradigm dlr reads and writes. Valid edits apply to the controls as you type.'}
          </p>
          {/* Quiet while it is fine: the YAML only speaks when it is wrong. */}
          {yamlState !== 'ok' && (
            <span className={`access__status is-${yamlState}`}>
              {yamlState === 'checking' && 'Checking…'}
              {yamlState === 'bad' && `${yamlErrors.length} problem${yamlErrors.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>

        <div className={`access__views access__views--${view}`}>
          {view !== 'code' && (
            <section className="access__pane">
              <RealmSchemaEditor
                key={editorKey}
                verbCaps={editorState.verbCaps}
                scopeFilters={editorState.scopeFilters}
                schemas={appSchemas || []}
                schemaGrants={form.schema_grants}
                onSchemaGrantsChange={g => setForm(p => ({ ...p, schema_grants: g }))}
                availablePlugins={availablePlugins}
                toolModes={dlrInputsToToolModes(form.dlr_inputs || [])}
                onToolModesChange={tm => setForm(p => ({
                  ...p, dlr_inputs: applyToolModesToDlrInputs(p.dlr_inputs || [], tm),
                }))}
                onChange={(verbCaps, scopeFilters) =>
                  setForm(p => ({ ...p, dlr_inputs: editorStateToDlrInputs(verbCaps, scopeFilters, p.dlr_inputs || []) }))
                }
                mode="edit"
                context="dlr"
                hideYamlToggle
                emptyHint="no access requested yet"
                nodeFilterLabel="Restrict to schema"
              />
            </section>
          )}

          {view !== 'visual' && (
            <section className="access__pane access__pane--code">
              {/* Above the editor, not below it: at 30 rows the list sat past the
                  fold, so the one thing you needed to read was the one thing you
                  could not see. */}
              {yamlErrors.length > 0 && (
                <ul className="access__errors">
                  {yamlErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              <Textarea
                className="access__yaml"
                value={draftYaml ?? generated}
                onChange={e => setDraftYaml(e.target.value)}
                rows={view === 'code' ? 24 : 22}
                spellCheck={false}
                placeholder="requests: []"
                aria-label="Access request as YAML"
              />
              <p className="access__hint">
                Schema names are resolved by the server, so this is the same text{' '}
                <code>paradigm dlr show</code> prints and <code>paradigm crux sync</code> produces.
                {draftYaml != null && (
                  <button className="access__revert" onClick={() => { setDraftYaml(null); setYamlErrors([]); setYamlState('ok'); }}>
                    Discard my edits
                  </button>
                )}
              </p>
            </section>
          )}
        </div>
      </section>

      {/* The stage button appears with the first edit, not before: a faded
          button with nothing to stage read as broken. */}
      {dirty && (
        <div className="access__stagebar" role="region" aria-label="Unsaved access changes">
          <div>
            <b>{summariseChange(JSON.parse(baseline), form.dlr_inputs || [], schemaNameMap)}</b>
            <span>People who already granted it keep what they granted until you commit.</span>
          </div>
          <div className="access__stagebar-acts">
            <button type="button" className="access__discard" onClick={reset} disabled={saving}>Discard</button>
            <Button onClick={() => save()} disabled={saving || yamlState === 'bad'}>
              {saving ? 'Staging…' : 'Stage changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// The editor stamps a generic label ("Discovery", "Activity") on what it adds.
// That is the resource's own name, not a reason, so it is not shown as one.
const isReason = (it) => Boolean(it.label)
  && it.label.trim().toLowerCase().replace(/[\s_-]+/g, '') !== String(it.resource).toLowerCase().replace(/_/g, '');

const RESOURCE_ICON = { nodes: Database, plugins: Plug, discovery: Users, activity: Activity, automations: Zap, sub_entity: Users };

/**
 * One line per request, worded as the consent screen words it: the verb's
 * label, what it covers (schemas or tools by name), and the reason the
 * developer gave, if any.
 */
export function AskList({ inputs, schemaNameMap = {}, plugins = [] }) {
  const pluginName = (id) => plugins.find((p) => String(p.id) === String(id))?.name || id;
  const rows = inputs.filter((it) => it && it.resource && it.verb);
  return (
    <ul className="access__asks" aria-label="What people are asked">
      {rows.map((it, i) => {
        const Icon = RESOURCE_ICON[it.resource] || Database;
        const label = VERB_LABELS[`${it.resource}.${it.verb}`] || `${it.resource} ${it.verb}`;
        const schemas = (it.schema_ids || []).map((id) => schemaNameMap[String(id)] || id);
        const tools = (it.plugin_ids || []).map(pluginName);
        const scope = [...schemas, ...tools];
        const wide = it.resource === 'nodes' && scope.length === 0;
        return (
          <li key={`${it.resource}.${it.verb}.${i}`} className="access__ask">
            <span className="access__ask-ic" aria-hidden="true"><Icon size={15} /></span>
            <span>
              <b>{label}{scope.length > 0 && <em> · {scope.join(', ')}</em>}{wide && <em> · every node</em>}</b>
              {isReason(it) && <span className="access__ask-why">{it.label}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A plugin is a third_parties row too; either marker says it is one. */
export const isPluginRow = (row) => Boolean(row && (row.is_plugin || row._type === 'plugin'));

/**
 * What an app asks people to approve. A PLUGIN NEVER STAGES HERE: what it
 * reads and writes is declared per tool, in its own workspace, and saved
 * through the plugin route — an app-style access draft would bypass both.
 */
function AccessTab(props) {
  if (isPluginRow(props.app)) {
    return (
      <div className="access">
        <p className="access__lede">
          A plugin’s reads and writes are set on its tools.{' '}
          <Link to={`/plugins/${props.app.id}`}>Open the plugin</Link>
        </p>
      </div>
    );
  }
  return <AppAccessTab {...props} />;
}

export default AccessTab;
